import { useState } from 'react';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import { AssignPlaylistButton, PlaylistAssignmentItemProgress, PlaylistAssignmentProgress, UnAssignPlaylistButton, useFilteredPlaylistAssignmentsForPortal } from '..';
import styled, { theme } from 'academy-common-lib/utils/Styling';
import { TABLE_PAGINATOR_SIZE } from '../../table';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import { useFullPlaylist } from '../../savedItems';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import HR from 'UIComponents/elements/HR';
import { useHasGate } from '../../accounts';

// a bit hacky but it is the only way to customise the sections
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledRow = styled(UIExpandableSection).attrs({
  use: 'enclosed'
}).withConfig({
  displayName: "PlaylistAssignmentsForPortalList__StyledRow",
  componentId: "sc-1roc1j1-0"
})(["& >:first-child{background-color:", ";}div[role='button'][aria-expanded]{> h2{width:100%;padding-right:", ";", ";}> span{", ";}}"], theme.colors.OLAF, ({
  $hasActions
}) => $hasActions ? '0' : '4px', ({
  $hasExpandableContent
}) => $hasExpandableContent ? '' : 'padding-left: 18px', ({
  $hasExpandableContent
}) => $hasExpandableContent ? '' : 'display: none');
const StyledMenuIcon = styled(UIIcon).withConfig({
  displayName: "PlaylistAssignmentsForPortalList__StyledMenuIcon",
  componentId: "sc-1roc1j1-1"
})(["font-size:20px;width:26px;height:26px;transform:translateY(16px);"]);
const PlaylistAssignmentsForPortalContent = ({
  assignment: {
    playlistId,
    portalId,
    assignee: {
      userId
    },
    progress,
    dueDate,
    id
  }
}) => {
  const {
    playlist
  } = useFullPlaylist(playlistId);
  const hasShortsGate = useHasGate('Academy:ShortFormContent');
  if (!playlist) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIFlex, {
      children: [/*#__PURE__*/_jsx(UIBox, {
        grow: 1,
        style: {
          fontWeight: '600'
        },
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.playlistAssignments.dueDate",
          options: {
            date: /*#__PURE__*/_jsx(AssignPlaylistButton, {
              assignmentIds: [id],
              playlistId: playlistId,
              portalId: portalId,
              use: "link",
              children: dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
                format: "L",
                type: "utc",
                value: dueDate
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.common.add"
              })
            })
          }
        })
      }), /*#__PURE__*/_jsx(UIBox, {
        className: "p-right-2",
        children: /*#__PURE__*/_jsx(UnAssignPlaylistButton, {
          assignments: [id],
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.playlistAssignments.removeUser"
          })
        })
      })]
    }), /*#__PURE__*/_jsx(HR, {
      distance: "small"
    }), /*#__PURE__*/_jsx(UIList, {
      childClassName: "m-bottom-4",
      lastChildClassName: "m-bottom-0",
      children: playlist.items.map(item => {
        if (!hasShortsGate && item.type === 'SHORT_FORM_CONTENT') return null;
        const itemProgress = progress && progress.itemsProgress.find(ip => ip.itemId === item.itemId);
        return /*#__PURE__*/_jsx(PlaylistAssignmentItemProgress, {
          item: item,
          playlistId: playlistId,
          progress: itemProgress
        }, `${playlistId}_${portalId}_${userId}_${item.itemId}`);
      })
    })]
  });
};
const PlaylistAssignmentsForPortalRow = ({
  assignment,
  renderRow = () => /*#__PURE__*/_jsx(PlaylistAssignmentProgress, {
    assignment: assignment
  }),
  renderOptions,
  renderRowContents = () => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalContent, {
    assignment: assignment
  }),
  onRowOpenChange
}) => {
  const handleOpenChange = e => {
    const isOpen = e.target.value;
    if (onRowOpenChange) onRowOpenChange({
      isOpen,
      assignment
    });
  };
  return /*#__PURE__*/_jsx(StyledRow, {
    $hasActions: !!renderOptions,
    $hasExpandableContent: !!renderRowContents,
    actions: renderOptions && /*#__PURE__*/_jsx(UIDropdown, {
      buttonUse: "transparent",
      caretRenderer: /*#__PURE__*/_jsx(StyledMenuIcon, {
        name: "verticalMenu"
      }),
      iconName: "",
      children: /*#__PURE__*/_jsx(UIList, {
        children: renderOptions(assignment)
      })
    }),
    "data-test-id": `row_${assignment.assignee.userId}`,
    onOpenChange: handleOpenChange,
    titleText: renderRow(assignment),
    children: renderRowContents && renderRowContents(assignment)
  });
};
const PlaylistAssignmentsForPortalList = ({
  playlistId,
  portalId,
  renderRow,
  renderRowContents,
  renderOptions,
  onRowOpenChange
}) => {
  const [page, setPage] = useState(1);
  const {
    playlistAssignmentsForPortal,
    pageCount
  } = useFilteredPlaylistAssignmentsForPortal({
    playlistId,
    portalId,
    page
  });
  if (!playlistAssignmentsForPortal) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("section", {
      className: "playlist-assignments-list",
      children: playlistAssignmentsForPortal.map(assignment => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalRow, {
        assignment: assignment,
        onRowOpenChange: onRowOpenChange,
        renderOptions: renderOptions,
        renderRow: renderRow,
        renderRowContents: renderRowContents
      }, `${playlistId}_${portalId}_${assignment.assignee.userId}`))
    }), pageCount > 1 && /*#__PURE__*/_jsx(UIPaginator, {
      className: "m-top-3",
      maxVisiblePageButtons: TABLE_PAGINATOR_SIZE,
      onPageChange: ({
        target: {
          value
        }
      }) => setPage(value),
      page: page,
      pageCount: pageCount
    })]
  });
};
export default PlaylistAssignmentsForPortalList;